import { apiClient, handleApiError } from './api.js';

class BillingService {
  // ==================== Billing Items ====================
  
  // Get all billing items
  async getAllBillingItems() {
    try {
      return await apiClient.get('/billing/items');
    } catch (error) {
      console.error('Error getting all billing items:', error);
      throw error;
    }
  }

  // Get billing item by ID
  async getBillingItemById(itemId) {
    try {
      return await apiClient.get(`/billing/items/${itemId}`);
    } catch (error) {
      console.error(`Error getting billing item ${itemId}:`, error);
      throw error;
    }
  }

  // Get billing items by category
  async getBillingItemsByCategory(category) {
    try {
      return await apiClient.get(`/billing/items/category/${category}`);
    } catch (error) {
      console.error(`Error getting billing items for category ${category}:`, error);
      throw error;
    }
  }

  // Create a new billing item
  async createBillingItem(billingItem) {
    try {
      // Prepare billing item data with user tracking fields
      const billingItemData = {
        ...billingItem,
        // Initialize navigation properties for user tracking
        // These will be populated by the backend based on the JWT token
        // Providing empty User objects with UserId property to satisfy model validation
        createdByUser: { userId: 0 },
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.post('/billing/items', billingItemData);
    } catch (error) {
      console.error('Error creating billing item:', error);
      throw error;
    }
  }

  // Update an existing billing item
  async updateBillingItem(itemId, billingItem) {
    try {
      // The backend will handle timestamp tracking and user tracking via the JWT token
      // UpdatedBy and UpdatedAt are extracted from the JWT token in the backend
      const billingItemData = {
        ...billingItem,
        // Initialize navigation property for user tracking
        // This will be populated by the backend based on the JWT token
        // Providing empty User object with UserId property to satisfy model validation
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.put(`/billing/items/${itemId}`, billingItemData);
    } catch (error) {
      console.error(`Error updating billing item ${itemId}:`, error);
      throw error;
    }
  }

  // Delete a billing item
  async deleteBillingItem(itemId) {
    try {
      return await apiClient.delete(`/billing/items/${itemId}`);
    } catch (error) {
      console.error(`Error deleting billing item ${itemId}:`, error);
      throw error;
    }
  }

  // ==================== Invoices ====================

  // Get all invoices
  async getAllInvoices() {
    try {
      return await apiClient.get('/billing/invoices');
    } catch (error) {
      console.error('Error getting all invoices:', error);
      throw error;
    }
  }

  // Get invoice by ID
  async getInvoiceById(invoiceId) {
    try {
      return await apiClient.get(`/billing/invoices/${invoiceId}`);
    } catch (error) {
      console.error(`Error getting invoice ${invoiceId}:`, error);
      throw error;
    }
  }

  // Get invoices by patient ID
  async getInvoicesByPatientId(patientId) {
    try {
      return await apiClient.get(`/billing/invoices/patient/${patientId}`);
    } catch (error) {
      console.error(`Error getting invoices for patient ${patientId}:`, error);
      throw error;
    }
  }

  // Get invoices by medical record ID
  async getInvoicesByMedicalRecordId(medicalRecordId) {
    try {
      return await apiClient.get(`/billing/invoices/medical-record/${medicalRecordId}`);
    } catch (error) {
      console.error(`Error getting invoices for medical record ${medicalRecordId}:`, error);
      throw handleApiError(error);
    }
  }

  // Get invoices by date range
  async getInvoicesByDateRange(startDate, endDate) {
    try {
      return await apiClient.get(`/billing/invoices/date-range?startDate=${startDate}&endDate=${endDate}`);
    } catch (error) {
      console.error('Error getting invoices by date range:', error);
      throw error;
    }
  }

  // Get invoices by status
  async getInvoicesByStatus(status) {
    try {
      return await apiClient.get(`/billing/invoices/status/${status}`);
    } catch (error) {
      console.error(`Error getting invoices with status ${status}:`, error);
      throw error;
    }
  }

  // Create a new invoice
  async createInvoice(invoice, paymentDetails) {
    try {
      const invoiceData = {
        invoice,
        paymentMethod: paymentDetails?.paymentMethod,
        paymentDate: paymentDetails?.paymentDate,
      };
      return await apiClient.post('/billing/invoices', invoiceData);
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw handleApiError(error);
    }
  }

  // Update an existing invoice
  async updateInvoice(invoiceId, invoice, paymentDetails) {
    try {
      const invoiceData = {
        invoice: {
          ...invoice,
          updatedByUser: { userId: 0 }
        },
        paymentMethod: paymentDetails?.paymentMethod,
        paymentDate: paymentDetails?.paymentDate
      };
      
      return await apiClient.put(`/billing/invoices/${invoiceId}`, invoiceData);
    } catch (error) {
      console.error(`Error updating invoice ${invoiceId}:`, error);
      throw error;
    }
  }

  // Delete an invoice
  async deleteInvoice(invoiceId) {
    try {
      return await apiClient.delete(`/billing/invoices/${invoiceId}`);
    } catch (error) {
      console.error(`Error deleting invoice ${invoiceId}:`, error);
      throw error;
    }
  }

  // Update invoice status
  async updateInvoiceStatus(invoiceId, status) {
    try {
      return await apiClient.put(`/billing/invoices/${invoiceId}/status`, { status });
    } catch (error) {
      console.error(`Error updating status for invoice ${invoiceId}:`, error);
      throw error;
    }
  }

  // Link an invoice to a medical record
  async linkInvoiceToMedicalRecord(invoiceId, medicalRecordId) {
    try {
      return await apiClient.post(`/billing/invoices/${invoiceId}/medical-record/${medicalRecordId}`);
    } catch (error) {
      console.error(`Error linking invoice ${invoiceId} to medical record ${medicalRecordId}:`, error);
      throw handleApiError(error);
    }
  }

  // Create invoice from appointment
  async createInvoiceFromAppointment(appointmentId) {
    try {
      return await apiClient.post(`/billing/invoices/from-appointment/${appointmentId}`);
    } catch (error) {
      console.error(`Error creating invoice from appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // Create invoice from medical record
  async createInvoiceFromMedicalRecord(medicalRecordId) {
    try {
      return await apiClient.post(`/billing/invoices/from-medical-record/${medicalRecordId}`);
    } catch (error) {
      console.error(`Error creating invoice from medical record ${medicalRecordId}:`, error);
      throw handleApiError(error);
    }
  }

  // Get invoices by appointment ID
  async getInvoicesByAppointmentId(appointmentId) {
    try {
      return await apiClient.get(`/billing/invoices/appointment/${appointmentId}`);
    } catch (error) {
      console.error(`Error getting invoices for appointment ${appointmentId}:`, error);
      throw handleApiError(error);
    }
  }

  // ==================== Invoice Items ====================

  // Get invoice items by invoice ID
  async getInvoiceItemsByInvoiceId(invoiceId) {
    try {
      return await apiClient.get(`/billing/invoices/${invoiceId}/items`);
    } catch (error) {
      console.error(`Error getting invoice items for invoice ${invoiceId}:`, error);
      throw error;
    }
  }

  // Get invoice item by ID
  async getInvoiceItemById(itemId) {
    try {
      return await apiClient.get(`/billing/invoice-items/${itemId}`);
    } catch (error) {
      console.error(`Error getting invoice item ${itemId}:`, error);
      throw error;
    }
  }

  // Create a new invoice item
  async createInvoiceItem(invoiceItem) {
    try {
      return await apiClient.post('/billing/invoice-items', invoiceItem);
    } catch (error) {
      console.error('Error creating invoice item:', error);
      throw error;
    }
  }

  // Update an existing invoice item
  async updateInvoiceItem(itemId, invoiceItem) {
    try {
      return await apiClient.put(`/billing/invoice-items/${itemId}`, invoiceItem);
    } catch (error) {
      console.error(`Error updating invoice item ${itemId}:`, error);
      throw error;
    }
  }

  // Delete an invoice item
  async deleteInvoiceItem(itemId) {
    try {
      return await apiClient.delete(`/billing/invoice-items/${itemId}`);
    } catch (error) {
      console.error(`Error deleting invoice item ${itemId}:`, error);
      throw error;
    }
  }

  // ==================== Payments ====================

  // Get all payments
  async getAllPayments() {
    try {
      return await apiClient.get('/billing/payments');
    } catch (error) {
      console.error('Error getting all payments:', error);
      throw error;
    }
  }

  // Get payment by ID
  async getPaymentById(paymentId) {
    try {
      return await apiClient.get(`/billing/payments/${paymentId}`);
    } catch (error) {
      console.error(`Error getting payment ${paymentId}:`, error);
      throw error;
    }
  }

  // Get payments by invoice ID
  async getPaymentsByInvoiceId(invoiceId) {
    try {
      return await apiClient.get(`/billing/payments/invoice/${invoiceId}`);
    } catch (error) {
      console.error(`Error getting payments for invoice ${invoiceId}:`, error);
      throw error;
    }
  }

  // Get payments by patient ID
  async getPaymentsByPatientId(patientId) {
    try {
      return await apiClient.get(`/billing/payments/patient/${patientId}`);
    } catch (error) {
      console.error(`Error getting payments for patient ${patientId}:`, error);
      throw error;
    }
  }

  // Get payments by date range
  async getPaymentsByDateRange(startDate, endDate) {
    try {
      return await apiClient.get(`/billing/payments/date-range?startDate=${startDate}&endDate=${endDate}`);
    } catch (error) {
      console.error('Error getting payments by date range:', error);
      throw error;
    }
  }

  // Create a new payment
  async createPayment(payment) {
    try {
      return await apiClient.post('/billing/payments', payment);
    } catch (error) {
      console.error('Error creating payment:', error);
      throw error;
    }
  }

  // Update an existing payment
  async updatePayment(paymentId, payment) {
    try {
      return await apiClient.put(`/billing/payments/${paymentId}`, payment);
    } catch (error) {
      console.error(`Error updating payment ${paymentId}:`, error);
      throw error;
    }
  }

  // Delete a payment
  async deletePayment(paymentId) {
    try {
      return await apiClient.delete(`/billing/payments/${paymentId}`);
    } catch (error) {
      console.error(`Error deleting payment ${paymentId}:`, error);
      throw error;
    }
  }
}

const billingService = new BillingService();
export default billingService;